import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ChevronDown, Circle } from "lucide-react";

const OPTIONS = [
  { key: "in_clinic", label: "In clinic", tone: "text-[#1B5E20] bg-emerald-100" },
  { key: "on_call",   label: "On call",   tone: "text-[#B45309] bg-amber-100" },
  { key: "off",       label: "Off",       tone: "text-slate-600 bg-slate-200" },
];

export function AvailabilityPill() {
  const { user } = useAuth();
  const [value, setValue] = useState("in_clinic");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "doctor") return;
    (async () => {
      try {
        const { data } = await api.get("/staff/me/availability");
        setValue(data.availability || "in_clinic");
      } catch { /* ignore */ }
    })();
  }, [user]);

  if (!user || user.role !== "doctor") return null;

  const setStatus = async (k) => {
    setOpen(false);
    if (k === value) return;
    try {
      setSaving(true);
      await api.post("/staff/me/availability", { availability: k });
      setValue(k);
      // Invalidate module-level cache used by bed-board dots so they update on the next render
      try { const m = await import("../pages/admitted/DoctorAvailabilityDot"); m.refreshDoctorAvailability?.(); } catch {}
      toast.success(`You are now ${OPTIONS.find((o) => o.key === k).label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const current = OPTIONS.find((o) => o.key === value) || OPTIONS[0];

  return (
    <div className="relative" data-testid="availability-pill">
      <button
        data-testid="availability-toggle"
        onClick={() => setOpen((v) => !v)}
        disabled={saving}
        className={`h-8 pl-2 pr-2.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${current.tone} hover:opacity-90`}
      >
        <Circle className="w-2 h-2 fill-current" />
        {current.label}
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-40 bg-white rounded-lg shadow-xl border border-slate-200 py-1 w-40" data-testid="availability-menu">
            {OPTIONS.map((o) => (
              <button
                key={o.key}
                data-testid={`availability-set-${o.key}`}
                onClick={() => setStatus(o.key)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${value === o.key ? "font-semibold text-slate-900" : "text-slate-700"}`}
              >
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${o.tone.replace("text-","bg-").split(" ")[1]}`} />
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
